/**
 * useFindingLinkedCAPs (P76.2).
 *
 * Wraps GET /v1/findings/{id}/linked-caps -- the back-link list of CAPs
 * that remediate a finding. The endpoint is module-framework gated
 * (CAP data), so a twin-only viewer of the FindingDetail page gets a
 * 403; the section degrades gracefully on that. No retry on 403/404.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { FindingLinkedCap } from "shared/api/types";

export const findingLinkedCapsKeys = {
  all: ["finding-linked-caps"] as const,
  byId: (id: string) => [...findingLinkedCapsKeys.all, id] as const,
};

export function useFindingLinkedCAPs(
  findingId: string | undefined,
  /** Round 17 G20 -- a seat without the Framework module is not asked
   *  for plans it cannot read: the owner's finding pages were refused. */
  mayRead = true,
): UseQueryResult<FindingLinkedCap[], ApiError> {
  return useQuery({
    queryKey: findingLinkedCapsKeys.byId(findingId ?? ""),
    queryFn: () => api.get<FindingLinkedCap[]>(`/v1/findings/${findingId}/linked-caps`),
    enabled: Boolean(findingId) && mayRead,
    staleTime: 30_000,
    retry: (_count, err) => {
      const status = (err as ApiError | undefined)?.status ?? 0;
      return status !== 403 && status !== 404;
    },
  });
}
